import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { HandHeart, Send, Heart, Trash2, Sparkles, Share2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EngagementBar from '../components/EngagementBar';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { sharePrayerAsImage } from '../utils/shareAsImage';

export default function PrayerWall() {
  const { user } = useAuth();
  const [prayers, setPrayers] = useState([]);
  const [content, setContent] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [prayedIds, setPrayedIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('prayedIds') || '[]');
    } catch {
      return [];
    }
  });

  useEffect(() => {
    loadPrayers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPrayers = async () => {
    try {
      const { data } = await api.get('/prayers');
      setPrayers(data);
    } catch (error) {
      console.error('Failed to load prayers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/prayers', { content, name: name.trim() || null });
      toast.success('Your prayer request has been shared');
      setContent('');
      setName('');
      loadPrayers();
    } catch (error) {
      toast.error('Failed to share prayer request');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePray = async (prayerId) => {
    if (prayedIds.includes(prayerId)) return;
    try {
      const { data } = await api.post(`/prayers/${prayerId}/pray`);
      setPrayers((prev) => prev.map((p) => (p.id === prayerId ? data : p)));
      const updated = [...prayedIds, prayerId];
      setPrayedIds(updated);
      localStorage.setItem('prayedIds', JSON.stringify(updated));
      toast.success('You lifted this prayer up');
    } catch (error) {
      toast.error('Failed to record your prayer');
    }
  };

  const [sharingId, setSharingId] = useState(null);
  const handleShare = async (prayer) => {
    setSharingId(prayer.id);
    try {
      const result = await sharePrayerAsImage(prayer);
      if (result.downloaded) toast.success('Prayer image saved to your device');
      else if (result.shared) toast.success('Thanks for sharing this prayer');
    } catch (err) {
      console.error(err);
      toast.error('Could not create share image');
    } finally {
      setSharingId(null);
    }
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <HandHeart className="w-10 h-10" style={{ color: '#78A18E', strokeWidth: 1.5 }} />
            <h1 className="text-4xl sm:text-5xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
              Prayer Wall
            </h1>
          </div>
          <p className="text-lg max-w-2xl mx-auto" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
            Share a prayer request, or quietly lift up someone else's. Every request matters. Every prayer is heard.
          </p>
        </div>

        <div className="p-8 rounded-3xl mb-12" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}>
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
            Post a Prayer Request
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontWeight: 600, fontSize: '14px' }}>
                Name (Optional)
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29' }}
                placeholder="First name or leave blank for anonymous"
                data-testid="prayer-name"
              />
            </div>
            <div>
              <label className="block mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontWeight: 600, fontSize: '14px' }}>
                Prayer Request
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 rounded-xl border"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29' }}
                placeholder="Share what you'd like others to lift up in prayer..."
                data-testid="prayer-content"
              />
              <p className="text-xs mt-1 text-right" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                {content.length}/500 characters
              </p>
            </div>
            <button
              type="submit"
              disabled={submitting || !content.trim()}
              className="w-full py-4 rounded-full transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#78A18E', color: '#FDFBF7', fontWeight: 600 }}
              data-testid="prayer-submit"
            >
              <Send className="w-5 h-5" />
              <span>{submitting ? 'Sharing...' : 'Share Prayer Request'}</span>
            </button>
          </form>
        </div>

        <div>
          <h2 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
            Community Prayer Requests {prayers.length > 0 && <span style={{ color: '#8C867D', fontSize: '18px', fontWeight: 400 }}>({prayers.length})</span>}
          </h2>
          {prayers.length === 0 ? (
            <div className="text-center py-16 rounded-3xl" style={{ backgroundColor: '#F3EFE7' }}>
              <HandHeart className="w-16 h-16 mx-auto mb-4" style={{ color: '#78A18E', strokeWidth: 1 }} />
              <p className="text-lg" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                Be the first to share a prayer request
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {prayers.map((prayer) => {
                const hasPrayed = prayedIds.includes(prayer.id);
                return (
                  <div key={prayer.id} className="p-6 rounded-2xl transition-all duration-300 relative" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)' }} data-testid={`prayer-card-${prayer.id}`}>
                    <div className="absolute top-3 right-3 flex items-center gap-2">
                      <button
                        onClick={() => handleShare(prayer)}
                        disabled={sharingId === prayer.id}
                        className="p-2 rounded-full transition-all duration-300 hover:scale-110 disabled:opacity-60 disabled:cursor-wait opacity-70 hover:opacity-100"
                        style={{ backgroundColor: '#78A18E15', color: '#5C715E' }}
                        aria-label="Share this prayer as an image"
                        title="Share as image"
                        data-testid={`share-prayer-${prayer.id}`}
                      >
                        <Share2 className="w-3.5 h-3.5" style={{ strokeWidth: 2.2 }} />
                      </button>
                      {user?.role === 'admin' && (
                        <button
                          onClick={async () => {
                            if (!window.confirm('Delete this prayer permanently? This also removes any comments and reactions on it.')) return;
                            try {
                              await api.delete(`/admin/prayers/${prayer.id}`);
                              toast.success('Prayer removed');
                              setPrayers((prev) => prev.filter((p) => p.id !== prayer.id));
                            } catch (err) { toast.error(err?.response?.data?.detail || 'Could not delete'); }
                          }}
                          className="p-2 rounded-full transition-all duration-300 hover:scale-110 opacity-70 hover:opacity-100"
                          style={{ backgroundColor: '#E57A4415', color: '#E57A44' }}
                          aria-label="Delete prayer"
                          title="Delete this prayer (admin only)"
                          data-testid={`admin-delete-prayer-${prayer.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="mb-4" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: '1.7' }}>
                      {prayer.content}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: '#F3EFE7' }}>
                      <div className="text-xs flex items-center gap-1.5 flex-wrap" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                        <span style={{ color: '#595553', fontWeight: 600 }}>{prayer.name}</span>
                        {prayer.is_sanctuary_member && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: '#78A18E15', color: '#5C715E', fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '10px', letterSpacing: '0.04em', border: '1px solid #78A18E30' }}
                            title="A Sanctuary member"
                            data-testid={`prayer-sanctuary-badge-${prayer.id}`}
                          >
                            <Sparkles className="w-2.5 h-2.5" style={{ strokeWidth: 2.5 }} />
                            <span>Sanctuary</span>
                          </span>
                        )}
                        <span>•</span>
                        <span>{new Date(prayer.created_at).toLocaleDateString()}</span>
                      </div>
                      <button
                        onClick={() => handlePray(prayer.id)}
                        disabled={hasPrayed}
                        className="flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 hover:scale-105 disabled:cursor-not-allowed"
                        style={{
                          fontFamily: 'Nunito, sans-serif',
                          backgroundColor: hasPrayed ? '#78A18E20' : '#F3EFE7',
                          color: hasPrayed ? '#78A18E' : '#595553',
                          fontWeight: 600,
                          fontSize: '13px'
                        }}
                        data-testid={`pray-button-${prayer.id}`}
                      >
                        <Heart className="w-4 h-4" style={{ fill: hasPrayed ? '#78A18E' : 'none', strokeWidth: 2 }} />
                        <span>{hasPrayed ? 'Prayed' : 'I prayed'}</span>
                        {prayer.prayed_count > 0 && <span style={{ color: '#78A18E', fontWeight: 700 }}>({prayer.prayed_count})</span>}
                      </button>
                    </div>
                    <EngagementBar parentType="prayer" parentId={prayer.id} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
